
import { useEffect } from 'react'; 
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'; 
import { Button } from '@/components/ui/button'; 
import { RefreshCw, AlertTriangle, Wifi, WifiOff } from 'lucide-react';

interface LoadingErrorBoundaryProps { 
  isLoading: boolean; 
  error: Error | null; 
  onRetry?: () => void; 
  loadingMessage?: string; 
  children: React.ReactNode; 
  connectionState?: 'disconnected' | 'connecting' | 'connected'; }

export const LoadingErrorBoundary = ({ isLoading, error, onRetry, loadingMessage = "Loading...", children, connectionState }: LoadingErrorBoundaryProps) => { useEffect(() => { if (error) { console.error('LoadingErrorBoundary caught error:', error);

  // Prevent cascading errors by limiting console output
  if (error.message?.includes('JSON') || error.message?.includes('fetch')) {
    console.warn('Network/JSON error detected, check server endpoints');
  }
}
}, [error]);

if (isLoading) { 
  return ( <div className="flex items-center justify-center h-64"> 
    <div className="flex flex-col items-center gap-4"> 
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div> 
      <p className="text-sm text-gray-500">{loadingMessage}</p> {connectionState && ( 
        <div className="flex items-center gap-2 text-xs text-gray-400"> {connectionState === 'connected' && <Wifi className="h-3 w-3 text-green-500" />} {connectionState === 'connecting' && <RefreshCw className="h-3 w-3 animate-spin" />} 
        {connectionState === 'disconnected' && <WifiOff className="h-3 w-3 text-red-500" />} <span>Realtime: {connectionState}
        </span> </div> )} 
    </div> </div> ); }

if (error) { 
  const isNetworkError = error.message?.includes('fetch') || error.message?.includes('JSON'); const errorTitle = isNetworkError ? 'Connection Issue' : 'Failed to load boards';
  const errorMessage = isNetworkError ? 'Unable to connect to the server. Please check your connection and try again.' : error.message?.includes('syntax error') ? 'There was a problem connecting to your boards. This might be a temporary issue.' : error.message || 'An unexpected error occurred while loading your boards.';

return (
  <div className="flex items-center justify-center h-64 p-4">
    <Alert className="max-w-md">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>{errorTitle}</AlertTitle>
      <AlertDescription className="mt-2 space-y-3">
        <p className="text-sm">{errorMessage}</p>
        {onRetry && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onRetry}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </Button>
        )}
        <p className="text-xs text-gray-500">
          If this issue persists, try refreshing the page or check your internet connection.
        </p>
      </AlertDescription>
    </Alert>
  </div>
);
}

return <>{children}</>; };